import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import "./challengesDashboard.scss";
import videoIcon from "../../../assets/video-icon.svg";
import documentIcon from "../../../assets/document-icon.svg";
import { IChallenges, ItemType } from "adapters/challenges.adapter";
import {
  findCurrentChallenge,
  isSubChallengesCompleted,
} from "utils/challengesUtils";
import { saveCompleteChallenge } from "services/routeServices";
import ChallengeCheck from "./challengeCheck";

import Lottie from "lottie-react";
import * as animacion from "./../../../assets/animacion.json";
import Modal from "../../../shared/src/components/molecules/modal/modal";
import Button from "../../atoms/button/button";
import "./modalChallenge.scss";

import { ComoSalirDeDeudas, FondoDeEmergencias, GastosHormiga, PorQueEsImportanteAhorrar, PresupuestoPersonal, ReglaDeAhorro, TenerUnSeguro, TriviaCreditosConProposito } from "pages";
import { createPortal } from "react-dom";
import MedalModal from "shared/src/components/organisms/medal-modal/MedalModal";
import medallaConquistador from "../../../assets/medallaConquistador.svg"
const baseURL = process.env.REACT_APP_REDIRECT_URL;

interface IChallengeDashboardProps {
  challenges: IChallenges[] | null;
  setChallenges: any;
  testMedalla?: boolean; // For testing only
  testDebug?: (debug: { currentChallenge: [number, number], isCurrentChallengeCorrect: boolean }) => void;
}
interface IMedalModalProps {
  handleClose: any;
  children?: any;
  open: boolean;
}
const ChallengesDashboard = (
  { challenges, setChallenges, testMedalla, testDebug }: IChallengeDashboardProps,
  props: IMedalModalProps
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentChallenge, setCurrentChallenge] = useState<[number, number]>([
    0, 0,
  ]);
  const [openedChallenges, setOpenedChallenges] = useState<Array<number>>([0]);
  const [mobileOpenChallenges, setMobileOpenChallenges] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [medalla, setMedalla] = useState(testMedalla ?? false);

  const calculatedCurrentChallenge = useMemo(() => {
    let challenge: any;
    if (challenges) {
      challenge = findCurrentChallenge(challenges);
    }
    return challenge;
  }, [challenges]);

  const currentSubChallenge = useMemo(() => {
    if (challenges) {
      const pos = challenges?.findIndex(item => item.idChallenge === (currentChallenge[0] || 0));
      return challenges[pos !== -1 ? pos : 0 ]?.subChallengeDTO?.[currentChallenge[1]] || 0;    
    }
    return null;
  }, [currentChallenge, challenges]);

  const isCurrentChallengeCorrect = useMemo(() => {
    if (calculatedCurrentChallenge) {
      return (
        currentChallenge[0] === calculatedCurrentChallenge.challengeDTO.idChallenge &&
        currentChallenge[1] === calculatedCurrentChallenge.subChallengeIndex
      );
    }
    return false;
  }, [calculatedCurrentChallenge, currentChallenge]);

  // Expose debug info for tests
  useEffect(() => {
    if (typeof testDebug === 'function') {
      testDebug({ currentChallenge, isCurrentChallengeCorrect });
    }
  }, [currentChallenge, isCurrentChallengeCorrect, testDebug]);

  const isCurrentChallengeComplete = useMemo(() => {
    if (challenges) {
      const pos = challenges?.findIndex(item => item.idChallenge === (currentChallenge[0] || 0));
      return challenges[pos !== -1 ? pos : 0 ]?.subChallengeDTO?.[currentChallenge[1]].completed || 0;
    }

    return false;
  }, [challenges, currentChallenge]);

  const toggleChallenges = (challengeId: number) => {
    if (openedChallenges.includes(challengeId)) {
      setOpenedChallenges(
        openedChallenges.filter((challenge) => challenge !== challengeId)
      );
      return;
    }
    setOpenedChallenges([...openedChallenges, challengeId]);
  };

  const handleSelectCurrentChallenge = (
    challengeId: number,
    challengeIndex: number
  ) => {
    setCurrentChallenge([challenges ? challenges[challenges.findIndex(item=>item.idChallenge === challengeId)].idChallenge : 0, challengeIndex]);
    const params = {
      challengeId: challenges ? challenges[challenges.findIndex(item=>item.idChallenge === challengeId)].idChallenge.toString() : "0",
      challengeIndex: challengeIndex.toString(),
    };

    const paramsResult = new URLSearchParams(params).toString();

    setSearchParams(paramsResult);
    setMobileOpenChallenges(!mobileOpenChallenges);
  };

  const handleNextChallenge = () => {
    if (!challenges) {
      return;
    }
    const challengeIdx = currentChallenge[0];
    const subChallengeIdx = currentChallenge[1];
    const positionChallenge = challenges.findIndex(item => item.idChallenge === challengeIdx)
    // If we are at the end of the sub-challenges, move to the next challenge at subchallenge index = 0
    if (
      subChallengeIdx ===
      challenges[positionChallenge]?.subChallengeDTO.length - 1
    ) {
      if (positionChallenge === challenges.length - 1) {
        return;
      }
      handleSelectCurrentChallenge(challenges[positionChallenge + 1]?.idChallenge, 0);
      return;
    }

    handleSelectCurrentChallenge(challenges[positionChallenge]?.idChallenge, subChallengeIdx + 1);
  };

  const handlePrevChallenge = () => {
    if (!challenges) {
      return;
    }
    const challengeIdx = currentChallenge[0];
    const subChallengeIdx = currentChallenge[1];
    const positionChallenge = challenges.findIndex(item => item.idChallenge === challengeIdx);
    // If we are the start of the sub-challenges move the challenge to the previous index
    if (subChallengeIdx === 0) {
      if (positionChallenge === 0) {
        return;
      }
      handleSelectCurrentChallenge(
        challenges[positionChallenge - 1]?.idChallenge,
        challenges[positionChallenge - 1]?.subChallengeDTO.length - 1
      );
      return;
    }

    handleSelectCurrentChallenge(challengeIdx, subChallengeIdx - 1);
  };

  const handleChallengeDone = async (
    challengeIdx: number,
    subChallengeIdx: number
  ) => {
    if (!challenges) {
      return;
    }

    if (!calculatedCurrentChallenge || !isCurrentChallengeCorrect) {
      return;
    }

    if (challenges[challenges.findIndex(item => item.idChallenge === challengeIdx)]?.subChallengeDTO[subChallengeIdx].completed) {
      return;
    }

    const challengesDummy = JSON.parse(JSON.stringify(challenges));

    try {
      // Await and catch errors from saveCompleteChallenge
      const response = await saveCompleteChallenge(
        challengeIdx,
        subChallengeIdx.toString()
      );
      if (!response || response.status !== 200) {
        window.alert("Ocurrió un error al guardar el desafío");
        return;
      }
      // Mark as completed before updating state
      challengesDummy[challenges.findIndex(item => item.idChallenge === challengeIdx)].subChallengeDTO[subChallengeIdx].completed = true;
      setChallenges(challengesDummy);
    } catch (error) {
      window.alert("Ocurrió un error al guardar el desafío");
    }
  };
  // Show modal for Reto 4 completion (all subchallenges complete, medalla false)
  useEffect(() => {
    if (
      challenges &&
      challenges.length >= 4 &&
      challenges[3].subChallengeDTO.every(s => s.completed) &&
      medalla === false
    ) {
      setShowModal(true);
    }
  }, [challenges, medalla]);

  const routeGames = ()=>{
    window.location.href=`${baseURL}/games`
  };

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (headerRef.current && !headerRef.current?.contains(event.target)) {
        setMobileOpenChallenges(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [headerRef]);

  useEffect(() => {
    const challengeId = searchParams.get("challengeId");
    const challengeIndex = searchParams.get("challengeIndex");

    if (challengeId && challengeIndex && challenges) {
      const challengeIndexNumber = parseInt(challengeIndex);
      const challengeIdNumber = parseInt(challengeId);
      setCurrentChallenge([challengeIdNumber, challengeIndexNumber]);
      setOpenedChallenges([challengeIdNumber]);
    }

  }, [challenges]);

  const handleMobileToggleChallenges = () => {
    setMobileOpenChallenges(!mobileOpenChallenges);
  };

  const handlePreviousRoute = () => {
    if (currentChallenge[0] === 0 && currentChallenge[1] === 0) {
      navigate("/");
      return;
    }
    handlePrevChallenge();
  };

  useEffect(() => {
    if (typeof testMedalla === 'boolean') {
      setMedalla(testMedalla);
      return;
    }
    if(!challenges) return;
    challenges[3].subChallengeDTO[1]?.completed === true && setMedalla(true)
  }, [challenges, testMedalla])
  

  if (!challenges) {
    return <></>;
  }

  return (
    <div className="challenge-dashboard">
      {/* Desktop challenges sidebar */}
      <aside className="challenge-dashboard-sidebar">
        <div>
          {challenges.map((challenge, idx) => {
            const isParentOpen = openedChallenges.includes(challenge.idChallenge);
            const isOwnChildSelected =
              currentChallenge[0] === challenge.idChallenge && currentChallenge[1] !== undefined;
            return (
              <div key={challenge.idChallenge}>
                <button
                  onClick={() => toggleChallenges(challenge.idChallenge)}
                  className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-parent ${
                    isOwnChildSelected
                      ? "challenge-dashboard-sidebar__button--active"
                      : ""
                  }`}
                >
                  <span>{`Reto ${idx + 1}`}</span>
                  <span className="material-icons">
                    {isParentOpen ? "expand_less" : "expand_more"}
                  </span>
                </button>
                <div
                  className={`challenge-dashboard-sidebar__challenge ${
                    isParentOpen
                      ? "challenge-dashboard-sidebar__challenge--open"
                      : ""
                  }`}
                >
                  {challenge.subChallengeDTO.map((child, childIdx) => {
                    const isChildActive =
                      currentChallenge[0] === challenge.idChallenge &&
                      currentChallenge[1] === childIdx;
                    return (
                      <button
                        key={child.title}
                        onClick={() =>
                          handleSelectCurrentChallenge(challenge.idChallenge, childIdx)
                        }
                        className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-child ${
                          isChildActive
                            ? "challenge-dashboard-sidebar__button-child--active"
                            : ""
                        }`}
                      >
                        <img
                          src={
                            child.type === ItemType.Text
                              ? documentIcon
                              : videoIcon
                          }
                          alt={child.title + " icon"}
                          width={32}
                          height={32}
                        />
                        <span>{child.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </aside>
      <main id="dashboard-content" className="challenge-dashboard-content">
        {/* Mobile challenges bar */}
        <div className="challenge-dashboard-header">
          <div
            className="challenge-dashboard-header__container"
            ref={headerRef}
          >
            <button
              className="challenge-dashboard-header__display-item"
              onClick={handleMobileToggleChallenges}
            >
              <ChallengeCheck
                checked={isSubChallengesCompleted(
                  challenges,
                  currentChallenge[0]
                )}
              />
              <span>Reto {challenges.findIndex(item => item.idChallenge === currentChallenge[0]) + 1}</span>
              <span className="material-icons">
                {mobileOpenChallenges ? "expand_less" : "expand_more"}
              </span>
            </button>
            {mobileOpenChallenges && (
              <div className="challenge-dashboard-header__items-container">
                {challenges.map((challenge, idx) => {
                  const isParentOpen = openedChallenges.includes(challenge.idChallenge);
                  const isOwnChildSelected =
                    currentChallenge[0] === challenge.idChallenge &&
                    currentChallenge[1] !== undefined;
                  return (
                    <div key={challenge.idChallenge}>
                      <button
                        className={`challenge-dashboard-header__item ${
                          isOwnChildSelected ||
                          isSubChallengesCompleted(challenges, challenge.idChallenge)
                            ? "challenge-dashboard-header__item--active"
                            : ""
                        }`}
                        onClick={() => toggleChallenges(challenge.idChallenge)}
                      >
                        <ChallengeCheck
                          checked={isSubChallengesCompleted(
                            challenges,
                            challenge.idChallenge
                          )}
                        />
                        <span>Reto {idx + 1}</span>
                        <span className="material-icons">
                          {isParentOpen ? "expand_less" : "expand_more"}
                        </span>
                      </button>
                      <div
                        className={`challenge-dashboard-header__item__content ${
                          isParentOpen
                            ? "challenge-dashboard-header__item__content--open"
                            : ""
                        }`}
                      >
                        {challenge.subChallengeDTO.map(
                          (subChallenge, subChallengeIdx) => {
                            const isChildActive =
                              currentChallenge[0] === challenge.idChallenge &&
                              currentChallenge[1] === subChallengeIdx;
                            return (
                              <button
                                key={subChallenge.title}
                                onClick={() =>
                                  handleSelectCurrentChallenge(
                                    challenge.idChallenge,
                                    subChallengeIdx
                                  )
                                }
                                className={`challenge-dashboard-sidebar__button challenge-dashboard-sidebar__button-child ${
                                  isChildActive
                                    ? "challenge-dashboard-sidebar__button-child--active"
                                    : ""
                                }`}
                              >
                                <img
                                  src={
                                    subChallenge.type === ItemType.Text
                                      ? documentIcon
                                      : videoIcon
                                  }
                                  alt={subChallenge.title + " icon"}
                                  width={32}
                                  height={32}
                                />
                                <span>{subChallenge.title}</span>
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="back-button__container">
            <button className="back-button" onClick={handlePreviousRoute}>
              <span className="material-icons md-18">arrow_back</span>
              <p>Volver</p>
            </button>
          </div>
          <div className="challenge-nav__center">
            <header className="challenge-nav">
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO.map(
                (subChallenge: any, idx: number) => (
                  <div
                    className="challenge-nav__item"
                    style={
                      {
                        backgroundColor: subChallenge.completed
                          ? "#EE2B7B"
                          : "#EEEEEE",
                        "--main-color": subChallenge.completed
                          ? "white"
                          : "#96A5BF",
                      } as React.CSSProperties
                    }
                    key={idx}
                  >
                    <span className="material-icons md-18">done</span>
                    <span className="challenge-nav__item__number">
                      {idx + 1}
                    </span>
                  </div>
                )
              )}
            </header>
          </div>
        </div>
        <div className="challenge-dashboard-content__container">
          {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]] &&
            challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].type === "0" && (
            <>
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === 'Cómo hacer un presupuesto personal en 5 pasos' ? <PresupuestoPersonal/> : '' }
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === 'Gastos hormiga: qué son y cómo evitarlos' ? <GastosHormiga/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === 'Conoce la regla de ahorro 50/30/20' ? <ReglaDeAhorro/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === 'Ahorrar: ¿cómo crear y mantener un fondo de emergencias?' ? <FondoDeEmergencias/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === '¿Por qué es importante adquirir un seguro?' ? <TenerUnSeguro/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === '¿Cómo salir de deudas? Conoce 5 estrategias' ? <ComoSalirDeDeudas/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === 'Finanzas con propósito ¿Necesitas o no un crédito?' ? <TriviaCreditosConProposito/> : ''}
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title === '¿Por qué es importante ahorrar? Te lo contamos con futbol' ? <PorQueEsImportanteAhorrar/> : ''}
            </>
          )}
          {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]] &&
            challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].type === "1" && (
            <div className="challenge-dashboard-video">
              <h2>{challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title}</h2>
              <div className="challenge-dashboard-video__container">
                <iframe
                  width="auto"
                  height="auto"
                  src={challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].url ?? ""}
                  title={challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <p>{challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[currentChallenge[1]].description}</p>
            </div>
          )}
          <div className="challenge-dashboard-content__controls">
            <button
              onClick={handlePrevChallenge}
              className={`challenge-dashboard-content__controls__before ${
                currentChallenge[0] === challenges.findIndex(item => item.idChallenge === currentChallenge[0]) && currentChallenge[1] === 1
                  ? "challenge-dashboard-content__controls__before--disabled"
                  : ""
              }`}
              disabled={challenges.findIndex(item => item.idChallenge === currentChallenge[0]) === 0 && currentChallenge[1] === 0 ? true:false}
            >
              <span className="material-icons">chevron_left</span>
              <span>Anterior</span>
            </button>
            <button
              disabled={
                // Only enable if current challenge is correct, or if it's Reto 4 Sub 2 for modal test
                !isCurrentChallengeCorrect && !(currentChallenge[0] === 4 && currentChallenge[1] === 1)
              }
              onClick={() =>
                handleChallengeDone(currentChallenge[0], currentChallenge[1])
              }
              className={`challenge-dashboard-content__controls__done ${
                isCurrentChallengeComplete
                  ? "challenge-dashboard-content__controls__done--active"
                  : ""
              } ${
                (!isCurrentChallengeCorrect && !(currentChallenge[0] === 4 && currentChallenge[1] === 1))
                  ? "challenge-dashboard-content__controls__done--disabled"
                  : ""
              }`}
            >
              {challenges[challenges.findIndex(item => item.idChallenge === currentChallenge[0])]?.subChallengeDTO[
                currentChallenge[1]
              ].completed ? (
                <>
                  <span className="material-icons md-18">done</span>
                  <span>Hecho</span>
                </>
              ) : (
                <span>Ya lo hice</span>
              )}
            </button>
              <>
              {challenges.findIndex(item => item.idChallenge === currentChallenge[0]) === 3 &&
                currentChallenge[1] === 1 ?
                <>
                <button
                    onClick={routeGames}
                    className="challenge-dashboard-content__controls__after">
                  <span>Terminar</span>
                  <span className="material-icons">chevron_right</span>
                </button>
                </>
              :
            <button
              onClick={handleNextChallenge}
              className="challenge-dashboard-content__controls__after"
            >
                <>
                  <span>Siguiente</span>
                  <span className="material-icons">chevron_right</span>
                </>
            </button>
              }
              </>
          </div>
        </div>
        {showModal && (
          <Modal data-testid="modal" open={showModal} handleClose={() => { setShowModal(false); }} >
            <div className="modal-svg">
              <Lottie className="iconFinanciera" animationData={animacion} />
            </div>
            <div>
              <h1 id="modal-title">¡Genial!</h1>
              <h3 id="modal-text">Superaste este desafío, continúa tu camino<br/> y desbloquea la medalla de Conquistador financiero.</h3>
              <Button gtmClass="secondary" text="" onClick={() => setShowModal(false)} >Continuar</Button>
            </div>
          </Modal>
        )}

        {  medalla && 

          createPortal(
            <MedalModal>
              <MedalModal.Icon element={<img className="medal-modal__icon" src={medallaConquistador} alt="Medalla Nomada" />} />
              <MedalModal.Title>¡Felicidades!</MedalModal.Title>
              <p><b>¡Desbloqueaste la medalla Conquistador financiero!</b></p>
              <p>Ahora, tienes herramientas a tu disposición y te estás preparando para gestionar tus finanzas personales de forma más eficiente y exitosa.</p>
            </MedalModal>,
            document.body
        )}
      
      </main>
    </div>
  );
};

export default ChallengesDashboard;
